import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const SemestresWidget = () => {
  const { user, API } = useContext(AuthContext);
  const [semestresData, setSemestresData] = useState({});
  const [semestresInfo, setSemestresInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSemestre, setSelectedSemestre] = useState(null);

  useEffect(() => {
    loadSemestresData();
  }, []);

  // Función para calcular el semestre actual basado en la fecha
  const calcularSemestreActual = () => {
    const hoy = new Date();
    const añoActual = hoy.getFullYear();
    const mesActual = hoy.getMonth() + 1; // getMonth() retorna 0-11
    
    // Año base es 2025 (Semestre 1)
    const añosDesdeInicio = añoActual - 2025;
    
    // Determinar si estamos en primer (Ene-Jun) o segundo semestre (Jul-Dic)
    const semestreEnAño = mesActual <= 6 ? 1 : 2;
    
    // Calcular número de semestre total
    const numeroSemestre = (añosDesdeInicio * 2) + semestreEnAño;
    
    return numeroSemestre;
  };

  useEffect(() => {
    // Seleccionar automáticamente el semestre actual basado en la fecha
    if (Object.keys(semestresData).length > 0) {
      const sortedSemesters = getAvailableSemesters();
      if (sortedSemesters.length > 0) {
        // Calcular semestre actual (basado en fecha)
        const semestreActual = calcularSemestreActual();
        
        // Verificar si el semestre actual existe en los datos
        const existeSemestreActual = sortedSemesters.includes(semestreActual);
        
        if (existeSemestreActual) {
          // Si existe, seleccionarlo
          setSelectedSemestre(semestreActual);
        } else {
          // Si no existe, usar el primero disponible (comportamiento anterior)
          setSelectedSemestre(sortedSemesters[0]);
        }
      }
    }
  }, [semestresData, semestresInfo]);

  const loadSemestresData = async () => {
    try {
      const response = await axios.get(`${API}/clases/por-semestre`);
      setSemestresData(response.data.semestres || {});
      setSemestresInfo(response.data.semestres_info || {});
    } catch (error) {
      toast.error('Error al cargar datos de semestres');
    } finally {
      setLoading(false);
    }
  };

  const getSemestreLabel = (num) => {
    const info = semestresInfo[num];
    if (info && typeof info === 'object') {
      // Usar el nombre original del semestre sin año
      return info.nombre || `Semestre ${num}`;
    }
    // Fallback si no hay info
    return `Semestre ${num}`;
  };

  // Get available semesters dynamically, sorted by year first, then by number
  const getAvailableSemesters = () => {
    const currentYear = new Date().getFullYear();
    
    const semestresArray = Object.keys(semestresData)
      .map(key => {
        const num = parseInt(key.replace('semestre_', ''));
        const data = semestresData[key];
        return {
          numero: num,
          ano: data.ano || 2025,
          nombre: data.nombre
        };
      });
    
    const sorted = semestresArray.sort((a, b) => {
      // First sort by year (current year first, then future years)
      if (a.ano !== b.ano) {
        // Prioritize current year, then sort ascending
        if (a.ano === currentYear) return -1;
        if (b.ano === currentYear) return 1;
        return a.ano - b.ano;
      }
      // If same year, sort by semester number
      return a.numero - b.numero;
    });
    
    return sorted.map(item => item.numero);
  };

  if (loading) {
    return (
      <Card className="shadow-lg border-0">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-xl">
          <i className="fas fa-graduation-cap text-purple-600 mr-3"></i>
          Clases por Semestre
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {getAvailableSemesters().length === 0 ? (
          <div className="text-center py-8">
            <i className="fas fa-graduation-cap text-gray-300 text-4xl mb-4"></i>
            <p className="text-gray-500">No hay semestres configurados</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {getAvailableSemesters().map((semestre) => {
            const data = semestresData[`semestre_${semestre}`] || { total_clases: 0, clases_estudiantes: 0, clases_profesores: 0 };
            const isSelected = selectedSemestre === semestre;
            const semestreActual = calcularSemestreActual();
            const isCurrentSemester = semestre === semestreActual;
            
            return (
              <div
                key={semestre}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 relative ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedSemestre(semestre)}
              >
                {/* Badge de "Actual" */}
                {isCurrentSemester && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-green-500 text-white text-xs px-2 py-1 shadow-md">
                      <i className="fas fa-calendar-check mr-1"></i>
                      Actual
                    </Badge>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                    {getSemestreLabel(semestre)}
                  </h3>
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {data.total_clases}
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Estudiantes:</span>
                      <span className="font-medium text-green-600">{data.clases_estudiantes}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Profesores:</span>
                      <span className="font-medium text-orange-600">{data.clases_profesores}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
              })}
            </div>

        {/* Selected Semester Details */}
        {selectedSemestre && semestresData[`semestre_${selectedSemestre}`] && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <i className="fas fa-list mr-2 text-purple-600"></i>
              Clases del {getSemestreLabel(selectedSemestre)}
            </h4>
            
            <div className="space-y-3">
              {semestresData[`semestre_${selectedSemestre}`].clases.length > 0 ? (
                semestresData[`semestre_${selectedSemestre}`].clases.map((clase, index) => (
                  <div
                    key={clase.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 text-sm truncate">
                        {clase.titulo}
                      </h5>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-gray-500">
                          Prof. {clase.profesor_nombre}
                        </span>
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            clase.categoria === 'estudiantes'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {clase.categoria === 'estudiantes' ? 'Estudiantes' : 'Profesores'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {clase.estudiantes_inscritos} inscritos
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-graduation-cap text-gray-300 text-3xl mb-3"></i>
                  <p>No hay clases en este semestre</p>
                </div>
              )}
            </div>
            
            {semestresData[`semestre_${selectedSemestre}`].clases.length > 0 && (
              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="btn-hover"
                  onClick={() => window.location.href = '/app/clases'}
                >
                  Ver todas las clases
                  <i className="fas fa-arrow-right ml-2"></i>
                </Button>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
};

export default SemestresWidget;